//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\HR\Analytics\HRAssessmentAnalytics.jsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    BarChart3,
    Layers,
    ChevronDown,
    ListChecks,
    SlidersHorizontal,
    ShieldAlert,
    Flag,
    Info,
    Ban,
    LayoutGrid,
} from "lucide-react";
import { getAssignedAssessmentQuestions } from "../../Services/CompanyServices";
import {
    getAssessmentFactor,
    getScoringConfig,
    getQuestionAnalytics,
} from "../../Services/CompanyAnalytics";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import HRAssessmentNavTabs from "../../../../Utility/HRAssessmentNavTabs";
import { sanitizeHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../Utility/sanitizeHtml";

const DEFAULT_SECTIONS = ["Section A", "Section B"];
const OPTION_BASED_TYPES = ["Multiple Choice", "Check Boxes", "Dropdown", "Rank"];
const SCALE_BASED_TYPES = ["Rating Bar", "Answer in Number"];

const CLASSIFICATION_OPTIONS = [
    { id: "Developmental", label: "Developmental", accent: "#d97706" },
    { id: "RedFlag", label: "Red flag", accent: "#dc2626" },
];

const getScoreShape = (optionType) => {
    if (OPTION_BASED_TYPES.includes(optionType)) return "options";
    if (SCALE_BASED_TYPES.includes(optionType)) return "scale";
    return "none";
};

const makeId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").trim();
};

const singleSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: 40,
        borderRadius: 8,
        borderColor: state.isFocused ? "#7f35b2" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(59,178,144,.20)" : "none",
        "&:hover": { borderColor: "#7f35b2" },
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuList: (provided) => ({ ...provided, padding: 0 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#7f35b2" : state.isFocused ? "#c2a8f026" : "#fff",
        color: state.isSelected ? "#fff" : "#111827",
        cursor: "pointer",
    }),
    placeholder: (provided) => ({ ...provided, color: "#6B7280", fontWeight: 500 }),
    indicatorSeparator: () => ({ display: "none" }),
    singleValue: (provided) => ({ ...provided, color: "#111827" }),
};

const ClassificationPicker = ({ value }) => (
    <div className="flex flex-wrap gap-1.5">
        {CLASSIFICATION_OPTIONS.map((option) => {
            const active = value === option.id;
            return (
                <span
                    key={option.id}
                    className="rounded-full border px-2.5 py-1 text-xs font-medium"
                    style={
                        active
                            ? { backgroundColor: option.accent, borderColor: option.accent, color: "#fff" }
                            : { backgroundColor: "#fff", borderColor: "#e5e7eb", color: "#4b5563" }
                    }
                >
                    {option.id === "RedFlag" && <Flag size={11} className="mr-1 inline -mt-0.5" />}
                    {option.label}
                </span>
            );
        })}
    </div>
);

const BandRangeInputs = ({ band }) => (
    <div className="flex items-center gap-1.5">
        <input
            type="number"
            value={band.min}
            disabled
            className="w-14 rounded-md border border-gray-200 px-2 py-1 text-xs bg-gray-50 text-gray-500"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
            type="number"
            value={band.max}
            disabled
            className="w-14 rounded-md border border-gray-200 px-2 py-1 text-xs bg-gray-50 text-gray-500"
        />
    </div>
);

const GridBandRangeInputs = ({ band }) => (
    <div className="flex items-center gap-2">
        <input
            type="number"
            value={band.min}
            disabled
            className="w-14 h-8 rounded-lg border border-gray-200 bg-gray-50 text-center text-xs font-semibold text-gray-500"
        />
        <span className="text-gray-400 font-medium">-</span>
        <input
            type="number"
            value={band.max}
            disabled
            className="w-14 h-8 rounded-lg border border-gray-200 bg-gray-50 text-center text-xs font-semibold text-gray-500"
        />
    </div>
);

const HRAssessmentAnalytics = () => {
    const location = useLocation();
    const { assessmentUID, assessmentName, description } = location.state || {};
    const [error, setError] = useState(null);

    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQuestion, setSelectedQuestion] = useState(0);
    const [assessmentFactor, setAssessmentFactor] = useState([]);
    const [selectedSection, setSelectedSection] = useState(DEFAULT_SECTIONS[0]);
    const [expandedSection, setExpandedSection] = useState(DEFAULT_SECTIONS[0]);
    const [viewMode, setViewMode] = useState("questions");

    const [factor1ID, setFactor1ID] = useState(null);
    const [factor2ID, setFactor2ID] = useState(null);
    const [factor1Bands, setFactor1Bands] = useState([]);
    const [factor2Bands, setFactor2Bands] = useState([]);
    const [grid, setGrid] = useState({});
    const [sectionBBands, setSectionBBands] = useState([]);
    const [redFlagReviewThreshold, setRedFlagReviewThreshold] = useState(null);
    const [configExists, setConfigExists] = useState(false);

    useEffect(() => {
        if (assessmentUID) {
            loadQuestions();
            loadAssessmentFactors();
            loadScoringConfig();
        }
    }, [assessmentUID]);

    // Auto-load per-question analytics whenever the selected question changes
    // (view-only: no manual enable/disable toggle, so this replaces it).
    useEffect(() => {
        const active = questions[selectedQuestion];
        if (active && active.scoreShape !== "none" && !active.analyticsLoaded) {
            loadQuestionAnalytics(active.questionUID, selectedQuestion);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedQuestion, questions.length]);

    const loadQuestions = async () => {
        try {
            setLoading(true);
            setError(null);
            const request = { surveyUID: assessmentUID };
            const response = await getAssignedAssessmentQuestions(request);

            if (response.data.status?.toLowerCase() === "success") {
                const mapped = (response.data.surveyDetails || []).map((q) => {
                    const scoreShape = getScoreShape(q.optionType);
                    return {
                        questionUID: q.uniqueKey,
                        questionName: q.questionName,
                        optionType: q.optionType,
                        scoreShape,
                        section: q.section || DEFAULT_SECTIONS[0],
                        channel: q.channel || "",
                        experienceType: q.experienceType || "",
                        questionSequence: q.questionSequence,
                        factor: [],
                        isAnalyticsEnabled: false,
                        analyticsLoaded: false,
                        options: (q.options || []).map((o) => ({
                            optionID: o.optionID,
                            optionName: o.optionName,
                            isGatekeeper: false,
                            score: 0,
                            classification: null,
                        })),
                        scoreBands: scoreShape === "scale" ? [] : undefined,
                    };
                });
                mapped.sort((a, b) => (a.questionSequence ?? 0) - (b.questionSequence ?? 0));
                setQuestions(mapped);
            }else {
            // Handle case where API returns HTTP 200, but response.data.status is not "success"
            setQuestions([]);
            setError("Failed to load questions. Please try again.");
        }
        } catch (error) {
            setQuestions([]);
            setError("An error occurred while fetching assessment questions.");
        } finally {
            setLoading(false);
        }
    };

    const loadAssessmentFactors = async () => {
        try {
            setError(null);
            const response = await getAssessmentFactor(assessmentUID);
            if (response.data.status?.toLowerCase() === "success") {
                setAssessmentFactor(response.data.assessmentFactors || []);
            }else {
                setAssessmentFactor([]);
                setError("Failed to load factors.");
                }       
        } catch (error) {
            setAssessmentFactor([]);
            setError("An error occurred while fetching assessment factors.");
        }
    };

    const loadScoringConfig = async () => {
        try {
            setError(null);
            const response = await getScoringConfig(assessmentUID);
            if (response.data.status?.toLowerCase() === "success" && response.data.config) {
                const config = response.data.config;
                setFactor1ID(config.factor1ID || null);
                setFactor2ID(config.factor2ID || null);
                setFactor1Bands(config.factor1Bands || []);
                setFactor2Bands(config.factor2Bands || []);
                setGrid(config.grid || {});
                setSectionBBands(
                    (config.sectionBBands || []).map((band) => ({ ...band, id: band.id || makeId() }))
                );
                setRedFlagReviewThreshold(config.redFlagReviewThreshold ?? null);
                setConfigExists(true);
            }
        } catch (error) {
            setError("An error occurred while fetching scoring configuration.");
        }
    };

    const loadQuestionAnalytics = async (questionUID, questionIndex) => {
        try {
            setError(null);
            const response = await getQuestionAnalytics(questionUID);
            if (response.data.status?.toLowerCase() !== "success") return;

            const analytics = response.data;
            const copy = [...questions];
            const question = { ...copy[questionIndex] };
            question.analyticsLoaded = true;

            if (question.scoreShape === "scale") {
                const savedBands = analytics.scoreBands || [];
                if (savedBands.length > 0) {
                    question.isAnalyticsEnabled = true;
                    question.scoreBands = savedBands.map((band) => ({
                        id: makeId(),
                        label: band.label ?? "Band",
                        min: band.min ?? 0,
                        max: band.max ?? 0,
                        score: band.score ?? 0,
                    }));
                }
                copy[questionIndex] = question;
                setQuestions(copy);
                return;
            }

            const factors = analytics.questionFactors || [];
            const savedOptions = analytics.options || [];
            const hasSavedAnalysis =
                factors.length > 0 ||
                savedOptions.some(
                    (o) => (o.score !== null && o.score !== undefined) || o.isGateKeeper || o.isDevelopment || o.isRedFlag
                );

            if (hasSavedAnalysis) {
                question.isAnalyticsEnabled = true;
                if (question.section === "Section A") {
                    question.factor = factors;
                    question.options = question.options.map((option) => {
                        const saved = savedOptions.find((x) => x.optionID === option.optionID);
                        return { ...option, score: saved?.score ?? 0, isGatekeeper: !!saved?.isGateKeeper };
                    });
                } else {
                    question.options = question.options.map((option) => {
                        const saved = savedOptions.find((x) => x.optionID === option.optionID);
                        let classification = null;
                        if (saved?.isDevelopment) classification = "Developmental";
                        else if (saved?.isRedFlag) classification = "RedFlag";
                        return { ...option, score: saved?.score ?? 0, classification };
                    });
                }
            }

            copy[questionIndex] = question;
            setQuestions(copy);
        } catch (error) {
            setError("An error occurred while fetching question analytics.");
        }
    };

    const selectQuestion = (globalIndex, section) => {
        setSelectedSection(section);
        setSelectedQuestion(globalIndex);
    };

    const toggleSection = (section) => {
        setSelectedSection(section);
        setExpandedSection((current) => (current === section ? null : section));
    };

    const sectionCounts = DEFAULT_SECTIONS.reduce((counts, section) => {
        counts[section] = questions.filter((q) => q.section === section).length;
        return counts;
    }, {});

    const activeQuestion = questions[selectedQuestion];

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-[#f7f8fa]">
            <div className="flex-shrink-0 px-0.5 pt-0.5">
                <div className="mx-auto max-w-[88rem]">
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">{assessmentName}</h1>
                    <HRAssessmentNavTabs
                        active="analytics"
                        assessmentUID={assessmentUID}
                        assessmentName={assessmentName}
                        description={description}
                    />
                </div>

                <div className="mx-auto flex max-w-[88rem] items-center py-4">
                    <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1">
                        <button
                            type="button"
                            onClick={() => setViewMode("questions")}
                            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${viewMode === "questions" ? "bg-white text-[#7f35b2] shadow-sm" : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            <ListChecks size={15} />
                            Question Scoring
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode("rules")}
                            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${viewMode === "rules" ? "bg-white text-[#7f35b2] shadow-sm" : "text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            <SlidersHorizontal size={15} />
                            Decision Rules
                        </button>
                    </div>
                </div>
            </div>

            {viewMode === "rules" ? (
                <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 shadow-xl bg-white rounded-xl mx-0.5">
                    <div className="mx-auto max-w-[88rem] space-y-6 pt-4">
                        {!configExists ? (
                            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
                                <SlidersHorizontal className="mx-auto mb-3 text-gray-300" size={32} />
                                Scoring rules haven't been configured for this assessment yet.
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4">
                                    <div className="px-1">
                                        <h2 className="text-lg font-semibold text-gray-900">Section A — Assessment Decision Grid</h2>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Rows represent the Y-Axis bands, columns represent the X-Axis bands. Cells where either
                                            axis is "Below Average" are automatically treated as "Do not proceed".
                                        </p>
                                    </div>

                                    <div className="flex flex-col xl:flex-row gap-4 items-start overflow-x-auto pb-2">
                                        <div className="w-full xl:w-[260px] my-auto flex items-center justify-center flex-col shrink-0 rounded-2xl bg-[#c2a8f01a] p-4 border border-[#c2a8f033] shadow-md">
                                            <div className="flex items-center gap-2 mb-4">
                                                <div className="p-1.5 bg-[#c2a8f033] rounded-md text-[#7f35b2]">
                                                    <BarChart3 size={16} />
                                                </div>
                                                <span className="text-sm font-semibold text-gray-800">Y Axis Factor (Rows)</span>
                                            </div>
                                            <Select
                                                isDisabled
                                                value={
                                                    assessmentFactor.find((item) => item.assessmentFactorID === factor1ID)
                                                        ? {
                                                            value: factor1ID,
                                                            label: assessmentFactor.find((item) => item.assessmentFactorID === factor1ID)
                                                                .assessmentFactor,
                                                        }
                                                        : null
                                                }
                                                placeholder="Assessment Factor"
                                                styles={singleSelectStyles}
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0 flex flex-col gap-4">
                                            <div className="flex items-center justify-center gap-3 rounded-2xl bg-[#c2a8f01a] p-2.5 border border-[#c2a8f033] shadow-md">
                                                <div className="flex items-center gap-2 pl-2">
                                                    <div className="p-1.5 bg-[#c2a8f033] rounded-md text-[#7f35b2]">
                                                        <LayoutGrid size={16} />
                                                    </div>
                                                    <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                                                        X Axis Factor (Columns)
                                                    </span>
                                                </div>
                                                <div className="flex-1 max-w-sm">
                                                    <Select
                                                        isDisabled
                                                        value={
                                                            assessmentFactor.find((item) => item.assessmentFactorID === factor2ID)
                                                                ? {
                                                                    value: factor2ID,
                                                                    label: assessmentFactor.find((item) => item.assessmentFactorID === factor2ID)
                                                                        .assessmentFactor,
                                                                }
                                                                : null
                                                        }
                                                        placeholder="Select X Axis Factor"
                                                        styles={singleSelectStyles}
                                                    />
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-lg">
                                                <table className="w-full border-separate border-spacing-3">
                                                    <thead>
                                                        <tr>
                                                            <th className="align-top text-left min-w-[180px] pb-2">
                                                                <div className="flex items-center gap-1.5 text-sm font-bold text-gray-800 pt-1 pl-1">
                                                                    Assessment Factor <Info size={14} className="text-gray-400" />
                                                                </div>
                                                            </th>
                                                            {factor2Bands.map((f2Band) => (
                                                                <th key={f2Band.id} className="min-w-[220px] text-left pb-2">
                                                                    <input
                                                                        value={f2Band.label}
                                                                        disabled
                                                                        className="w-full px-2 py-1.5 text-sm font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-md mb-2"
                                                                    />
                                                                    <GridBandRangeInputs band={f2Band} />
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {factor1Bands.map((f1Band) => (
                                                            <tr key={f1Band.id}>
                                                                <td className="align-top pt-4 pb-3 pr-4 pl-1">
                                                                    <input
                                                                        value={f1Band.label}
                                                                        disabled
                                                                        className="w-full px-2 py-1.5 text-sm font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-md mb-2"
                                                                    />
                                                                    <GridBandRangeInputs band={f1Band} />
                                                                </td>

                                                                {factor2Bands.map((f2Band) => {
                                                                    const cell = grid[f1Band.id]?.[f2Band.id] || { grade: "", title: "", description: "" };
                                                                    const mustNotProceed = f1Band.id === "below" || f2Band.id === "below";

                                                                    let cellStyle = "border-gray-200";
                                                                    let badgeStyle = "bg-gray-100 text-gray-700";
                                                                    if (mustNotProceed) {
                                                                        cellStyle = "border-red-200";
                                                                        badgeStyle = "bg-red-100 text-red-600";
                                                                    } else if (f2Band.id === "above") {
                                                                        cellStyle = "border-green-200";
                                                                        badgeStyle = "bg-green-100 text-green-700";
                                                                    }

                                                                    return (
                                                                        <td key={f2Band.id} className={`align-top rounded-xl border p-4 ${cellStyle}`}>
                                                                            <div className="mb-2">
                                                                                <span
                                                                                    className={`inline-flex w-10 h-8 items-center justify-center rounded-md text-center text-xs font-bold ${badgeStyle}`}
                                                                                >
                                                                                    {cell.grade}
                                                                                </span>
                                                                            </div>
                                                                            <p className="text-sm font-bold text-gray-800 mb-1">{cell.title}</p>
                                                                            <p className="text-xs text-gray-600 leading-snug">{cell.description}</p>
                                                                            {mustNotProceed && (
                                                                                <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-red-500">
                                                                                    <Ban size={12} strokeWidth={2.5} />
                                                                                    DO NOT PROCEED (AUTOMATIC)
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                                    <h2 className="text-lg font-semibold text-gray-900">Section B — Score Bands & Red Flags</h2>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Final recommendation bands based on total accumulated marks in Section B.
                                    </p>
                                    <div className="mt-4 space-y-2">
                                        {sectionBBands.map((band) => (
                                            <div
                                                key={band.id}
                                                className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2"
                                            >
                                                <input
                                                    value={band.label}
                                                    disabled
                                                    className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-500"
                                                />
                                                <BandRangeInputs band={band} />
                                            </div>
                                        ))}
                                    </div>
                                    {redFlagReviewThreshold != null && (
                                        <p className="mt-4 text-xs text-gray-500">
                                            Red-flag review threshold: <span className="font-semibold text-gray-700">{redFlagReviewThreshold}</span>
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className="min-h-0 flex-1 overflow-hidden pb-6 px-0.5">
                    <div className="flex h-full max-w-[88rem] gap-3 mx-auto">
                        <aside className="flex h-full w-[320px] flex-shrink-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                            <div className="flex-shrink-0 p-5 pb-0">
                                <div className="rounded-xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                                    <div className="mb-1 flex items-start justify-between gap-2">
                                        <h2 className="text-base font-semibold leading-tight text-gray-900 line-clamp-1">{assessmentName}</h2>
                                        <span className="flex-shrink-0 rounded-full bg-[#7f35b2]/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[#7f35b2]">
                                            Preview
                                        </span>
                                    </div>
                                    <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                    <div
                                        className="description-rich-content text-xs text-gray-600 line-clamp-5"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                                    />
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-5 pt-4">
                                <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Sections</div>
                                <div className="space-y-2">
                                    {DEFAULT_SECTIONS.map((section) => {
                                        const isSelected = selectedSection === section;
                                        const isExpanded = expandedSection === section;
                                        const sectionList = questions.filter((q) => q.section === section);

                                        return (
                                            <div
                                                key={section}
                                                className={`rounded-xl border transition-colors ${isSelected ? "border-[#7f35b2]/30 bg-[#7f35b2]/5" : "border-transparent bg-gray-50"
                                                    }`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSection(section)}
                                                    aria-expanded={isExpanded}
                                                    className="flex w-full items-center justify-between px-3 py-2.5"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Layers size={14} className={isSelected ? "text-[#7f35b2]" : "text-gray-400"} />
                                                        <span className={`text-sm font-medium ${isSelected ? "text-[#7f35b2]" : "text-gray-700"}`}>
                                                            {section}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isSelected ? "bg-[#7f35b2] text-white" : "bg-gray-200 text-gray-600"
                                                                }`}
                                                        >
                                                            {sectionCounts[section] || 0}
                                                        </span>
                                                        <motion.span animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="text-gray-400">
                                                            <ChevronDown size={14} />
                                                        </motion.span>
                                                    </div>
                                                </button>

                                                <AnimatePresence initial={false}>
                                                    {isExpanded && (
                                                        <motion.div
                                                            key="content"
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: "auto", opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                                            className="max-h-[200px] overflow-y-auto"
                                                        >
                                                            <div className="space-y-1 px-2 pb-2">
                                                                {sectionList.map((question) => {
                                                                    const globalIndex = questions.findIndex((q) => q.questionUID === question.questionUID);
                                                                    const hasGatekeeper =
                                                                        question.section === "Section A" && question.options.some((o) => o.isGatekeeper);
                                                                    const hasRedFlag =
                                                                        question.section === "Section B" &&
                                                                        question.options.some((o) => o.classification === "RedFlag");
                                                                    const isNotScoreable = question.scoreShape === "none";

                                                                    return (
                                                                        <div
                                                                            key={question.questionUID}
                                                                            role="button"
                                                                            tabIndex={0}
                                                                            onClick={() => selectQuestion(globalIndex, section)}
                                                                            className={`group flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${isNotScoreable ? "opacity-60" : ""
                                                                                } cursor-pointer ${selectedQuestion === globalIndex ? "bg-white shadow-sm" : "hover:bg-white"}`}
                                                                        >
                                                                            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500">
                                                                                {sectionList.indexOf(question) + 1}
                                                                            </span>
                                                                            <div className="min-w-0 flex-1">
                                                                                <div className="flex items-center justify-between gap-1">
                                                                                    <span className="line-clamp-1 text-xs text-gray-600">
                                                                                        {stripHtml(question.questionName) || "Untitled Question"}
                                                                                    </span>
                                                                                    <div className="flex flex-shrink-0 items-center gap-1">
                                                                                        {hasGatekeeper && (
                                                                                            <ShieldAlert size={11} className="text-amber-500" title="Has a gatekeeper option" />
                                                                                        )}
                                                                                        {hasRedFlag && <Flag size={11} className="text-red-500" title="Has a red-flag option" />}
                                                                                        {isNotScoreable && (
                                                                                            <span
                                                                                                className="flex flex-shrink-0 items-center gap-0.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] text-gray-500"
                                                                                                title="This question type can't be scored"
                                                                                            >
                                                                                                <Ban size={9} />
                                                                                                Not scoreable
                                                                                            </span>
                                                                                        )}
                                                                                        {question.isAnalyticsEnabled && (
                                                                                            <span className="flex-shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] text-green-700">
                                                                                                Analytics
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-gray-400">
                                                                                    <span>{question.channel}</span>
                                                                                    {question.experienceType && (
                                                                                        <>
                                                                                            <span>·</span>
                                                                                            <span>{question.experienceType}</span>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </aside>

                        <main className="flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                            {loading ? (
                                <div className="flex-1 animate-pulse bg-gray-50" />
                            ) : questions.length > 0 && activeQuestion ? (
                                <>
                                    <div className="sticky top-0 z-10 flex items-start justify-between border-b border-gray-100 bg-white px-3 py-4">
                                        <div className="flex items-center gap-4">
                                            <div>
                                                {(() => {
                                                    const sectionQuestions = questions.filter(q => q.section === activeQuestion.section);
                                                    const localIndex = sectionQuestions.findIndex(q => q.questionUID === activeQuestion.questionUID) + 1;
                                                    return (
                                                        <div className="flex gap-4">
                                                            <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white bg-[#7f35b2]">
                                                                {localIndex}
                                                            </div>
                                                            <div>
                                                                <h2 className="text-lg font-semibold">Question {localIndex}</h2>
                                                                <p className="text-sm text-gray-500">{stripHtml(activeQuestion.questionName)}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                    {activeQuestion.scoreShape === "none" ? (
                                                        <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                                            <Ban size={11} />
                                                            Not scoreable
                                                        </span>
                                                    ) : activeQuestion.isAnalyticsEnabled ? (
                                                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Analytics Enabled</span>
                                                    ) : (
                                                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Analytics Disabled</span>
                                                    )}
                                                    <span className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-600">{activeQuestion.section}</span>
                                                    <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">{activeQuestion.optionType}</span>
                                                    {activeQuestion.channel && (
                                                        <span className="px-2 py-1 text-xs rounded-full bg-purple-50 text-purple-600">{activeQuestion.channel}</span>
                                                    )}
                                                    {activeQuestion.experienceType && (
                                                        <span className="px-2 py-1 text-xs rounded-full bg-amber-50 text-amber-600">{activeQuestion.experienceType}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {activeQuestion.isAnalyticsEnabled && activeQuestion.scoreShape === "options" && activeQuestion.section === "Section A" && (
                                            <div className="w-72">
                                                <label className="text-xs font-medium text-gray-500 block mb-2">Assessment Factors</label>
                                                <Select
                                                    isMulti
                                                    isDisabled
                                                    controlShouldRenderValue={false}
                                                    placeholder={
                                                        activeQuestion.factor.length === 0
                                                            ? "No factors selected"
                                                            : `${activeQuestion.factor.length} Factor(s) Selected`
                                                    }
                                                    value={assessmentFactor
                                                        .filter((item) => activeQuestion.factor.includes(item.assessmentFactorID))
                                                        .map((item) => ({ value: item.assessmentFactorID, label: item.assessmentFactor }))}
                                                    styles={singleSelectStyles}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {activeQuestion.scoreShape === "none" ? (
                                        <div className="flex-1 flex items-center justify-center bg-gray-50">
                                            <div className="text-center max-w-md">
                                                <div className="w-20 h-20 mx-auto rounded-full bg-gray-200 flex items-center justify-center">
                                                    <Ban className="text-gray-500" size={32} />
                                                </div>
                                                <h3 className="mt-5 text-lg font-semibold text-gray-700">Not scoreable</h3>
                                                <p className="mt-2 text-sm text-gray-500">
                                                    "{activeQuestion.optionType}" questions collect free-text answers and don't have a
                                                    fixed answer set, so they can't be scored here.
                                                </p>
                                            </div>
                                        </div>
                                    ) : !activeQuestion.isAnalyticsEnabled ? (
                                        <div className="flex-1 flex items-center justify-center bg-gray-50">
                                            <div className="text-center max-w-md">
                                                <div className="w-20 h-20 mx-auto rounded-full bg-gray-200 flex items-center justify-center">
                                                    <BarChart3 className="text-gray-500" size={38} />
                                                </div>
                                                <h3 className="mt-5 text-lg font-semibold text-gray-700">Analytics Disabled</h3>
                                                <p className="mt-2 text-sm text-gray-500">
                                                    This question hasn't been configured for scoring yet.
                                                </p>
                                            </div>
                                        </div>
                                    ) : activeQuestion.scoreShape === "scale" ? (
                                        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                                            <div className="space-y-2">
                                                {(activeQuestion.scoreBands || []).map((band) => (
                                                    <div key={band.id} className="flex items-center gap-2 rounded-xl border bg-white px-4 py-3">
                                                        <input
                                                            value={band.label}
                                                            disabled
                                                            className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm bg-gray-50 text-gray-500"
                                                        />
                                                        <BandRangeInputs band={band} />
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-xs text-gray-500">Score</span>
                                                            <input
                                                                type="number"
                                                                disabled
                                                                value={band.score}
                                                                className="w-16 rounded-md border text-center py-1.5 text-sm bg-gray-50 text-gray-500"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : activeQuestion.section === "Section A" ? (
                                        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                                            {activeQuestion.options.map((option, optionIndex) => (
                                                <div key={optionIndex} className="rounded-xl border bg-white px-4 py-3">
                                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                                        <div className="flex items-center gap-4 text-sm w-[50%]">
                                                            {activeQuestion.optionType === "Multiple Choice" || activeQuestion.optionType === "Dropdown" ? (
                                                                <input type="radio" disabled className="accent-[#7f35b2]" />
                                                            ) : (
                                                                <input type="checkbox" disabled className="accent-[#7f35b2]" />
                                                            )}
                                                            <span className="font-medium">{option.optionName}</span>
                                                        </div>

                                                        <div className="flex items-center gap-4">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs text-gray-500">Score</span>
                                                                <input
                                                                    type="number"
                                                                    disabled
                                                                    value={option.score ?? 0}
                                                                    className="w-16 rounded-md border text-center py-1.5 text-sm bg-gray-50 text-gray-500"
                                                                />
                                                            </div>
                                                            <span
                                                                className="flex items-center gap-1.5 text-xs font-medium"
                                                                title="Gatekeeper option — selecting it screens the candidate out regardless of total score"
                                                            >
                                                                <ShieldAlert size={13} className={option.isGatekeeper ? "text-red-500" : "text-gray-300"} />
                                                                Gatekeeper
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                                            {activeQuestion.options.map((option, optionIndex) => (
                                                <div
                                                    key={optionIndex}
                                                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3"
                                                >
                                                    <div className="flex items-center gap-4 text-sm w-[50%]">
                                                        {activeQuestion.optionType === "Multiple Choice" || activeQuestion.optionType === "Dropdown" ? (
                                                            <input type="radio" disabled className="accent-[#7f35b2]" />
                                                        ) : (
                                                            <input type="checkbox" disabled className="accent-[#7f35b2]" />
                                                        )}
                                                        <span className="font-medium">{option.optionName}</span>
                                                    </div>

                                                    <div className="flex items-center gap-4">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-500">Score</span>
                                                            <input
                                                                type="number"
                                                                disabled
                                                                value={option.score ?? 0}
                                                                className="w-16 rounded-md border text-center py-1.5 text-sm bg-gray-50 text-gray-500"
                                                            />
                                                        </div>
                                                        <ClassificationPicker value={option.classification} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-gray-50 text-center">
                                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                                        <BarChart3 className="text-gray-400" size={30} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-700">No questions found</h3>
                                        <p className="mt-1 max-w-xs text-xs text-gray-500">
                                            This assessment doesn't have any questions yet.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HRAssessmentAnalytics;
